import { ArrowDownLeft, ArrowUpRight, CircleHelp } from 'lucide-react';
import type { LiveCallResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import {
  callerName,
  directionLabel,
  displayedElapsed,
  formatElapsed,
  reasonLabel,
  toneAnnouncement,
  toneStyle,
} from './format';

interface LiveCallRowProps {
  call: LiveCallResponse;
  isSelected: boolean;
  /** When the roster this row came from was fetched (ms epoch). */
  fetchedAt: number;
  /** Shared ticker (ms epoch) so every clock on the page moves together. */
  now: number;
  onSelect: () => void;
}

/**
 * One call in the left-hand roster: who, which way it's going and why,
 * then where it has got to.
 *
 * Direction and reason are CHIPS rather than an icon, because on a list
 * of a dozen calls "outbound referral" is the thing that distinguishes
 * two otherwise identical rows, and an arrow glyph makes the reader
 * decode it. The chips take the row's tone so a flagged or escalated call
 * doesn't sit under neutral-looking metadata.
 */
export function LiveCallRow({
  call,
  isSelected,
  fetchedAt,
  now,
  onSelect,
}: LiveCallRowProps): React.ReactElement {
  const tone = toneStyle(call.tone);
  const direction = directionLabel(call.direction);
  const DirectionIcon =
    direction === 'Inbound' ? ArrowDownLeft : direction === 'Outbound' ? ArrowUpRight : CircleHelp;
  const reason = reasonLabel(call.reason);
  const elapsed = displayedElapsed(call.elapsedSeconds, fetchedAt, now);
  const announcement = toneAnnouncement(call.tone);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'w-full px-3.5 py-2.5 text-left border-l-[3px] transition-colors',
        'flex flex-col gap-1',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected ? 'border-l-primary bg-accent' : 'border-l-transparent hover:bg-accent/50',
        !isSelected && tone.row,
        // A line that hasn't been picked up yet is present but not
        // competing for attention with the calls that have.
        !call.connected && 'opacity-65'
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold truncate">{callerName(call)}</span>
        {announcement ? <span className="sr-only">{announcement}</span> : null}
        <span className="flex-grow" />
        <span className={cn('font-mono text-xs tabular-nums font-semibold', tone.text)}>
          {formatElapsed(elapsed)}
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-px text-[10.5px] font-semibold',
            direction === 'Inbound' && 'bg-success/15 text-success',
            direction === 'Outbound' && 'bg-primary/10 text-primary',
            direction === 'Unknown' && 'bg-muted text-muted-foreground'
          )}
        >
          <DirectionIcon className="h-2.5 w-2.5" strokeWidth={3} />
          {direction}
        </span>
        {reason ? (
          <span className="rounded bg-muted px-1.5 py-px text-[10.5px] font-medium text-muted-foreground">
            {reason}
          </span>
        ) : null}
      </div>

      <div
        className={cn(
          'text-[11px] truncate',
          call.tone === 'normal' ? 'text-muted-foreground' : cn('font-semibold', tone.text)
        )}
      >
        {call.connected
          ? call.stage.label
          : `Ringing${call.attemptCount > 1 ? ` · attempt ${call.attemptCount}` : ''}`}
      </div>
    </button>
  );
}
